/* eslint-disable react/prop-types */
import React from 'react';
import { ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import Keys from 'lodash/keys';
// import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';

import { C, F } from '../constants/theme';
import { Btn, Heading, IconBox, OptionCard, Sub } from '../components';

import { LIST_BLOODWORK_MARKER_CATEGORIES } from 'core/api/apollo/queries';
import { SET_BLOODWORK_MARKERS } from 'core/api/apollo/mutations';
import { useProgramContext } from 'core/api/programContext';

const Phase4Biomarkers = ({
  bioOption,
  goPhase,
}) => {
  const { bioFlags, setBioFlags, toggleFlag, flagCount } = useProgramContext();
  const { data: { listBloodworkMarkerCategories = [] } = {} } = useQuery(LIST_BLOODWORK_MARKER_CATEGORIES);

  const [setBloodworkMarkers] = useMutation(SET_BLOODWORK_MARKERS, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Bloodwork markers set successfully:', data);
      goPhase(5);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in setting bloodwork markers:', error);
    },
  });

  const handleSetBloodworkMarkers = async () => {
    try {
      await setBloodworkMarkers({
        variables: {
          input: {
            markers: Keys(bioFlags).map((id) => ({ bloodworkMarkerId: id, status: bioFlags[id] })),
          },
        },
      });
    } catch (error) {
      console.error('Failed to set bloodwork markers:', error);
      throw error;
    }
  };

  // eslint-disable-next-line no-console
  console.log('listBloodworkMarkerCategories', listBloodworkMarkerCategories);
  // eslint-disable-next-line no-console
  console.log('bioFlags', bioFlags);

  return <>
    {bioOption === null && <>
      <Heading>Got bloodwork?</Heading>
      <Sub>Even a few flags help us match meals more precisely to your priorities.</Sub>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* <OptionCard onClick={() => goPhase(4, 0, 'upload')}>
          <IconBox name="flask"/>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.darkBrown }}>Upload or paste your results</div>
            <div style={{ fontSize: 12, color: C.lightBrown }}>
              Upload a PDF or screenshot, or paste your results. We'll pull out what we need.
            </div>
          </div>
          <ChevronRight size={16} color={C.lightBrown}/>
        </OptionCard> */}
        <OptionCard onClick={() => goPhase(4, 0, 'flag')}>
          <IconBox name="target"/>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.darkBrown }}>
              I know what needs work
            </div>
            <div style={{ fontSize: 12, color: C.lightBrown }}>
              Flag what your doctor's mentioned.
            </div>
          </div>
          <ChevronRight size={16} color={C.lightBrown}/>
        </OptionCard>
        <OptionCard onClick={() => { setBioFlags({}); setBloodworkMarkers({ variables: { input: { markers: [] } } }); }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.darkBrown }}>
              Skip for now
            </div>
            <div style={{ fontSize: 12, color: C.lightBrown }}>
              Goals and preferences alone build a great profile.
            </div>
          </div><ChevronRight size={16} color={C.lightBrown}/>
        </OptionCard>
        <div style={{ fontSize: 11, color: C.lightBrown, marginTop: 12, textAlign: 'center', lineHeight: 1.5 }}>
          Your data stays yours. Biomarker data is never sold. It's encrypted and used only to personalize your Methodology experience.
        </div>
      </div>
    </>}
    {bioOption === 'upload' && <>
      <Heading>Share your lab results</Heading>
      <Sub>We'll scan for biomarkers we can use. Your data is never shared or stored beyond building your plan.</Sub>
      <div style={{ border: '2px dashed #E8E4DC', borderRadius: 0, padding: '40px 20px', textAlign: 'center', marginBottom: 16 }}>
        <Upload size={28} color={C.lightBrown}/>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.darkBrown, marginTop: 10 }}>
          Drag and drop your lab report here, or tap to browse
        </div>
        <div style={{ fontSize: 12, color: C.lightBrown, marginTop: 4 }}>
          PDF, PNG, JPG · Most lab portals let you download results as PDF.
        </div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.darkBrown, marginBottom: 6 }}>Or paste your results</div>
      <textarea
        placeholder="Paste your lab results here. Copy the full results page from your patient portal—don't worry about formatting, we'll find the values."
        style={{ width: '100%', minHeight: 100, padding: 12, border: '1.5px solid #E8E4DC', borderRadius: 0, fontFamily: F.b, fontSize: 13, color: C.darkBrown, resize: 'vertical', boxSizing: 'border-box' }}
      />
      <div style={{ fontSize: 11, color: C.lightBrown, marginTop: 6, marginBottom: 16 }}>
        Tip: Most portals have a "Copy" or "Print" option on your results page.
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Btn primary={false} onClick={() => goPhase(4, 0, null)}><ChevronLeft size={15}/> Back</Btn>
        <Btn onClick={() => goPhase(4, 0, 'flag')} style={{ flex: 1 }}>Scan My Results <ChevronRight size={15}/></Btn>
      </div>
    </>}
    {bioOption === 'flag' && <>
      <Heading>What has your doctor flagged?</Heading>
      <Sub>Tap any marker that's been an issue. You don't need exact numbers.</Sub>
      <div style={{ fontSize: 11, color: C.lightBrown, marginBottom: 16 }}>
        Tap once = Too High · Tap again = Too Low · Tap again = Clear
      </div>
      {listBloodworkMarkerCategories.map((cat) => <div key={cat.id} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '1.2px', color: C.darkBrown, textTransform: 'uppercase', marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid #E8E4DC' }}>{cat.name}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {cat.bloodworkMarkers.map((m) => {
            const state = bioFlags[m.id];
            return <button key={m.id} onClick={() => toggleFlag(m.id)}
              style={{
                padding: '7px 12px', borderRadius: 0, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: F.b,
                border: `1.5px solid ${state === 'TOO_HIGH' ? C.concern : state === 'TOO_LOW' ? C.mustard : '#E8E4DC'}`,
                background: state === 'TOO_HIGH' ? C.concernBg : state === 'TOO_LOW' ? C.warningBg : C.pureWhite,
                color: state ? C.darkBrown : C.brown,
              }}>
              {state === 'TOO_HIGH' ? '⬆️ ' : state === 'TOO_LOW' ? '⬇️ ' : ''}{m.name}
            </button>;
          })}
        </div>
      </div>)}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <Btn primary={false} onClick={() => goPhase(4, 0, null)}><ChevronLeft size={15}/> Back</Btn>
        <Btn onClick={handleSetBloodworkMarkers} style={{ flex: 1 }}>
          {flagCount > 0 ? `Continue with ${flagCount} markers flagged` : "Everything's normal—continue"} <ChevronRight size={15}/>
        </Btn>
      </div>
    </>}
  </>;
};

export default Phase4Biomarkers;
